import { clsx } from 'clsx';

import { Typography } from '@/ui';

import AnswerDelta from './AnswerDelta';

import type { PulseReportDelta } from './deltaTypes';
import type { PulseReportAnswer } from '@/types';
import type { FC } from 'react';

interface AnswerRange10Props {
  answer: Extract<PulseReportAnswer, { kind: 'range10' }>;
  delta?: PulseReportDelta;
}

const BUCKET_CLASS_BY_KEY = {
  high: 'bg-transparent',
  low: 'bg-poppy-500',
  mid: 'bg-sunny-500',
} as const;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const AnswerRange10: FC<AnswerRange10Props> = ({ answer, delta }) => {
  const range = answer.max - answer.min;
  const ratio =
    range > 0 ? (answer.value - answer.min) / range : answer.value / answer.max;
  const width = `${Math.round(clamp(ratio, 0, 1) * 100)}%`;
  const minimumVisibleFill = answer.value <= answer.min ? '8px' : undefined;
  const scoreLabel = `${answer.value}/${answer.max}`;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-5 gap-y-2">
      <div className="min-w-0">
        <Typography className="min-w-0 text-cement-600" variant="subtitle1">
          {answer.prompt}
        </Typography>
      </div>
      <Typography
        className="whitespace-nowrap text-cement-600"
        data-testid="range-score"
        variant="subtitle1"
      >
        {scoreLabel}
      </Typography>
      {delta ? (
        <AnswerDelta className="font-bold" delta={delta} variant="subtitle1" />
      ) : (
        <span />
      )}
      <div
        className="col-span-2 h-2 w-full rounded bg-cement-200"
        data-testid="range-bar"
      >
        <div
          className={clsx('h-full rounded', BUCKET_CLASS_BY_KEY[answer.bucket])}
          style={{
            backgroundColor: answer.bucket === 'high' ? '#88D38D' : undefined,
            minWidth: minimumVisibleFill,
            width,
          }}
        />
      </div>
    </div>
  );
};

export default AnswerRange10;
